import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { FileDown, Save, Wand2, Loader2, Plus, Menu, ArrowLeft, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle, ImageRun } from "docx";
import { DashboardLayout } from "@/components/DashboardLayout";
import { useJobContext } from "@/contexts/JobContext";

interface ResumeSection {
  title: string;
  items: {
    company?: string;
    title?: string;
    start?: string;
    end?: string;
    bullets: string[];
  }[];
}

interface ResumeData {
  summary: string;
  sections: ResumeSection[];
  skills: string[];
  education: {
    institution: string;
    degree: string;
    years: string;
  }[];
}

export default function Resume() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedJob } = useJobContext();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [resumeData, setResumeData] = useState<ResumeData | null>(null);
  const [template, setTemplate] = useState<'classic' | 'modern' | 'minimal' | 'executive' | 'creative' | 'technical' | 'professional' | 'contemporary'>('modern');
  const [credits, setCredits] = useState(0);
  const [userName, setUserName] = useState<string>("");
  const [userProfile, setUserProfile] = useState<any>(null);
  const [includePhoto, setIncludePhoto] = useState(false); // Default OFF for ATS compliance

  // Auto-populate from JobContext - store target job title
  useEffect(() => {
    if (selectedJob) {
      toast({
        title: "Job loaded!",
        description: `Generate a resume tailored for ${selectedJob.title} at ${selectedJob.company}`,
      });
    }
  }, [selectedJob]);

  useEffect(() => {
    checkAuth();
    loadCredits();
    loadUserProfile();
    checkScannedJob();
  }, []);

  const checkScannedJob = () => {
    const scannedJobStr = sessionStorage.getItem('scannedJob');
    if (scannedJobStr) {
      try {
        const scannedJob = JSON.parse(scannedJobStr);
        toast({
          title: "Job Scanned!",
          description: `${scannedJob.title} at ${scannedJob.company} - Generate your resume, then check the ATS score with this job description.`,
          duration: 6000,
        });
        // Keep it in sessionStorage so it can be used in ATS checker after resume generation
      } catch (error) {
        console.error('Failed to load scanned job:', error);
      }
    }
  };

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/login');
    }
  };

  const loadCredits = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data } = await supabase
      .from('credits')
      .select('total_credits')
      .eq('user_id', session.user.id)
      .single();

    if (data) setCredits(data.total_credits);
  };

  const loadUserProfile = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .single();

    if (data) {
      setUserName(data.full_name || 'Your Name');
      setUserProfile(data);
    }
  };

  const generateResume = async () => {
    setGenerating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Load user data
      const [profileRes, workRes, eduRes, skillsRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', session.user.id).single(),
        supabase.from('work_experiences').select('*').eq('user_id', session.user.id),
        supabase.from('educations').select('*').eq('user_id', session.user.id),
        supabase.from('skills').select('*').eq('user_id', session.user.id)
      ]);

      // Generate idempotent refId
      const refId = `resume:${session.user.id}:${Date.now()}`;

      // Phase 1: Charge credits atomically
      const { data: creditResult, error: creditError } = await supabase.functions.invoke('credits-spend', {
        body: { featureKey: 'resume.generate_draft', refId }
      });

      if (creditError) {
        const errorData = creditError as any;
        if (errorData.error === 'INSUFFICIENT_CREDITS') {
          toast({
            variant: "destructive",
            title: "Insufficient credits",
            description: errorData.message || 'Not enough credits to generate resume',
          });
          return;
        }
        throw creditError;
      }

      // Phase 2: Execute AI generation
      const { data, error } = await supabase.functions.invoke('ai-generate-resume', {
        body: {
          profile: profileRes.data,
          workExperiences: workRes.data || [],
          educations: eduRes.data || [],
          skills: skillsRes.data || []
        }
      });

      if (error) throw error;

      setResumeData(data.resumeJson);
      
      // Show success with credit info
      if (!creditResult.idempotent && creditResult.cost > 0) {
        toast({
          title: "Resume generated!",
          description: `Used ${creditResult.cost} credit. ${creditResult.remaining} remaining.`,
        });
      } else {
        toast({
          title: "Resume generated!",
          description: "Your ATS-optimized resume is ready for review.",
        });
      }

      // Refresh credits display
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
    } catch (error: any) {
      console.error('[RESUME] Generation error:', error);
      toast({
        variant: "destructive",
        title: "Generation failed",
        description: error.message || "Failed to generate resume. Please try again.",
      });
    } finally {
      setGenerating(false);
    }
  };

  const saveVersion = async () => {
    if (!resumeData) return;
    
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { error } = await supabase.from('resume_versions').insert([{
        user_id: session.user.id,
        resume_json: { ...resumeData, userName, userProfile } as any,
        template,
        ats_score: calculateATSScore(resumeData)
      }]);

      if (error) throw error;

      toast({
        title: "Resume saved",
        description: "Your resume has been saved under Documents.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = async () => {
    if (!resumeData) return;

    try {
      // Create a printable HTML version
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast({
          variant: "destructive",
          title: "Export failed",
          description: "Please allow popups to export to PDF",
        });
        return;
      }

      const getTemplateStyles = () => {
        const baseFont = {
          'classic': 'Georgia, serif',
          'modern': 'Arial, Helvetica, sans-serif',
          'minimal': 'Calibri, system-ui, -apple-system, sans-serif',
          'executive': 'Garamond, serif',
          'creative': 'Verdana, sans-serif',
          'technical': '"Courier New", Courier, monospace',
          'professional': 'Times New Roman, serif',
          'contemporary': 'Segoe UI, Tahoma, sans-serif'
        }[template];

        const headerStyles = {
          'classic': 'border-bottom: 2pt solid #1f2937 !important; text-align: center !important;',
          'modern': 'border-bottom: 3pt solid #2563EB !important;',
          'minimal': 'border-bottom: 1pt solid #d1d5db !important;',
          'executive': 'border-bottom: 3pt solid #1a1a1a !important; background: #f8f8f8 !important; padding: 24pt 12pt !important;',
          'creative': 'border-left: 6pt solid #EC4899 !important; padding-left: 12pt !important; background: linear-gradient(90deg, #FDF4FF 0%, white 100%) !important;',
          'technical': 'border: 2pt solid #10b981 !important; padding: 12pt !important; background: #f0fdf4 !important;',
          'professional': 'border-bottom: 2pt solid #1e40af !important; background: #eff6ff !important; padding: 16pt 12pt !important;',
          'contemporary': 'border-bottom: 3pt solid #8b5cf6 !important; background: linear-gradient(180deg, #f5f3ff 0%, white 100%) !important; padding: 16pt 0 !important;'
        }[template];

        const h1Styles = {
          'classic': 'font-size: 32pt !important; color: #000 !important;',
          'modern': 'font-size: 28pt !important; color: #2563EB !important;',
          'minimal': 'font-size: 24pt !important; color: #000 !important;',
          'executive': 'font-size: 36pt !important; color: #1a1a1a !important; letter-spacing: 1pt !important;',
          'creative': 'font-size: 32pt !important; color: #EC4899 !important; font-weight: 900 !important;',
          'technical': 'font-size: 28pt !important; color: #059669 !important; font-weight: 600 !important;',
          'professional': 'font-size: 30pt !important; color: #1e40af !important;',
          'contemporary': 'font-size: 28pt !important; color: #7c3aed !important; font-weight: 700 !important;'
        }[template];

        const h2Styles = {
          'classic': 'text-align: center !important; border-bottom: 2pt solid #d1d5db !important;',
          'modern': 'border-left: 3pt solid #2563EB !important; padding-left: 6pt !important; color: #2563EB !important;',
          'minimal': 'border-bottom: 1pt solid #d1d5db !important; color: #000 !important;',
          'executive': 'border-bottom: 2pt solid #1a1a1a !important; color: #1a1a1a !important; background: #f8f8f8 !important; padding: 8pt 6pt !important; margin-left: -6pt !important; margin-right: -6pt !important;',
          'creative': 'border-left: 4pt solid #EC4899 !important; padding-left: 8pt !important; color: #EC4899 !important; background: #FDF4FF !important; padding: 6pt 8pt !important; margin-left: -8pt !important;',
          'technical': 'border: 1pt solid #10b981 !important; padding: 6pt 8pt !important; background: #f0fdf4 !important; color: #059669 !important; font-family: "Courier New", monospace !important;',
          'professional': 'border-bottom: 2pt solid #1e40af !important; color: #1e40af !important; background: #eff6ff !important; padding: 6pt 8pt !important; margin-left: -8pt !important; margin-right: -8pt !important;',
          'contemporary': 'border-left: 3pt solid #8b5cf6 !important; padding-left: 8pt !important; color: #7c3aed !important; background: linear-gradient(90deg, #f5f3ff 0%, white 100%) !important; padding: 6pt 8pt !important; margin-left: -8pt !important;'
        }[template];

        return { baseFont, headerStyles, h1Styles, h2Styles };
      };

      const { baseFont, headerStyles, h1Styles, h2Styles } = getTemplateStyles();

      const styles = `
        @page { 
          margin: 0.5in; 
          size: letter portrait;
        }
        @media print {
          html, body { 
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        * { 
          -webkit-print-color-adjust: exact !important; 
          print-color-adjust: exact !important;
          box-sizing: border-box !important;
        }
        body { 
          font-family: ${baseFont} !important; 
          background: white !important;
          color: black !important;
          margin: 0 !important;
          padding: 0 !important;
          font-size: 11pt !important;
          line-height: 1.4 !important;
        }
        .header { 
          padding-bottom: 12pt !important; 
          margin-bottom: 12pt !important;
          ${headerStyles}
        }
        .profile-photo {
          border-radius: 50% !important;
          object-fit: cover !important;
          width: ${template === 'executive' ? '80pt' : template === 'classic' ? '72pt' : '60pt'} !important;
          height: ${template === 'executive' ? '80pt' : template === 'classic' ? '72pt' : '60pt'} !important;
          margin-bottom: 8pt !important;
          ${template === 'creative' ? 'border: 4pt solid #EC4899 !important;' : ''}
          ${template === 'technical' ? 'border: 3pt solid #10b981 !important;' : ''}
          ${template === 'contemporary' ? 'border: 3pt solid #8b5cf6 !important;' : ''}
        }
        h1 { 
          ${h1Styles}
          margin: 0 0 4pt 0 !important; 
          padding: 0 !important;
          font-weight: 700 !important;
          line-height: 1.2 !important;
        }
        .headline { 
          color: #6b7280 !important; 
          font-size: 11pt !important; 
          margin-top: 4pt !important;
          line-height: 1.3 !important;
          ${template === 'creative' ? 'font-style: italic !important;' : ''}
          ${template === 'executive' ? 'font-weight: 500 !important; color: #4b5563 !important;' : ''}
        }
        .contact { 
          color: #6b7280 !important; 
          font-size: 10pt !important; 
          margin-top: 6pt !important;
          line-height: 1.3 !important;
        }
        h2 { 
          ${h2Styles}
          font-size: 14pt !important;
          font-weight: 700 !important;
          margin-top: 16pt !important;
          margin-bottom: 8pt !important;
          padding-bottom: 4pt !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5pt !important;
          line-height: 1.2 !important;
        }
        .summary { 
          font-size: 11pt !important; 
          line-height: 1.5 !important;
          margin-bottom: 8pt !important;
          ${template === 'classic' || template === 'executive' ? 'text-align: center !important;' : ''}
          ${template === 'creative' ? 'font-style: italic !important; padding-left: 12pt !important; border-left: 3pt solid #FDF4FF !important;' : ''}
        }
        .job { 
          margin-bottom: 12pt !important; 
          page-break-inside: avoid !important;
          ${template === 'modern' || template === 'creative' ? 'padding-left: 12pt !important;' : ''}
          ${template === 'technical' ? 'border-left: 2pt solid #d1fae5 !important; padding-left: 12pt !important;' : ''}
        }
        .job-title { 
          font-weight: 700 !important; 
          font-size: ${template === 'modern' || template === 'executive' ? '12pt' : '11pt'} !important;
          line-height: 1.3 !important;
          margin-bottom: 2pt !important;
        }
        .company { 
          font-size: 11pt !important; 
          color: #4b5563 !important; 
          font-style: italic !important;
          line-height: 1.3 !important;
          margin-bottom: 2pt !important;
        }
        .dates { 
          font-size: 10pt !important; 
          color: #6b7280 !important;
          line-height: 1.3 !important;
        }
        ul { 
          list-style-type: ${template === 'classic' ? 'none' : 'disc'} !important; 
          padding-left: ${template === 'classic' ? '0' : '15pt'} !important; 
          margin-top: 6pt !important;
          margin-bottom: 6pt !important;
        }
        li {
          font-size: 11pt !important;
          line-height: 1.4 !important;
          margin-bottom: 3pt !important;
          ${template === 'classic' ? 'padding-left: 12pt !important;' : ''}
        }
        ${template === 'classic' ? 'li::before { content: "•"; margin-right: 8px; }' : ''}
        p {
          font-size: 11pt !important;
          line-height: 1.4 !important;
          margin: 6pt 0 !important;
        }
        .skills { 
          font-size: 11pt !important; 
          ${template === 'modern' || template === 'creative' ? 'padding-left: 16pt !important;' : ''}
          ${template === 'classic' || template === 'executive' ? 'text-align: center !important;' : ''}
          ${template === 'technical' ? 'font-family: "Courier New", monospace !important;' : ''}
        }
      `;

      let html = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${userName} - Resume</title>
            <style>${styles}</style>
          </head>
          <body>
            <div class="header">
              <div style="display: flex; align-items: start; gap: 24px; ${template === 'classic' ? 'flex-direction: column; align-items: center;' : ''}">
                ${userProfile?.avatar_url ? `<img src="${userProfile.avatar_url}" alt="${userName}" class="profile-photo" />` : ''}
                <div style="flex: 1; ${template === 'classic' ? 'text-align: center;' : ''}">
                  <h1>${userName}</h1>
                  ${userProfile?.headline ? `<p class="headline">${userProfile.headline}</p>` : ''}
                  ${userProfile?.phone || userProfile?.location ? `
                    <div class="contact" style="${template === 'classic' ? 'display: flex; justify-content: center; gap: 16px;' : 'display: flex; gap: 16px;'}">
                      ${userProfile?.phone ? `<span>${userProfile.phone}</span>` : ''}
                      ${userProfile?.location ? `<span>${userProfile.location}</span>` : ''}
                      ${userProfile?.linkedin_url ? `<span>LinkedIn</span>` : ''}
                    </div>
                  ` : ''}
                </div>
              </div>
            </div>
            
            <h2>Professional Summary</h2>
            <p class="summary">${resumeData.summary}</p>
      `;

      for (const section of resumeData.sections || []) {
        html += `<h2>${section.title}</h2>`;
        for (const item of section.items || []) {
          html += `
            <div class="job">
              <div style="display: flex; justify-content: space-between; align-items: start;">
                <div>
                  <p class="job-title">${item.title}</p>
                  <p class="company">${item.company}</p>
                </div>
                <p class="dates">${item.start} - ${item.end}</p>
              </div>
              <ul>
                ${item.bullets.map(b => `<li>${b}</li>`).join('')}
              </ul>
            </div>
          `;
        }
      }

      if (resumeData.education && resumeData.education.length > 0) {
        html += '<h2>Education</h2>';
        for (const edu of resumeData.education) {
          html += `
            <div class="job">
              <div style="display: flex; justify-content: space-between; align-items: start;">
                <div>
                  <p class="job-title">${edu.degree}</p>
                  <p class="company">${edu.institution}</p>
                </div>
                <p class="dates">${edu.years}</p>
              </div>
            </div>
          `;
        }
      }

      if (resumeData.skills.length > 0) {
        html += `<h2>Skills</h2><p class="skills">${resumeData.skills.join(' • ')}</p>`;
      }

      html += '</body></html>';

      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 250);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast({
        variant: "destructive",
        title: "Export failed",
        description: "Failed to export to PDF format",
      });
    }
  };

  const exportToWord = async () => {
    if (!resumeData) return;

    try {
      const sections = [];
      const fontFamily = {
        'classic': 'Georgia',
        'modern': 'Arial',
        'minimal': 'Calibri',
        'executive': 'Garamond',
        'creative': 'Verdana',
        'technical': 'Courier New',
        'professional': 'Times New Roman',
        'contemporary': 'Segoe UI'
      }[template] || 'Arial';
      
      // Create header section with photo and name side by side
      const headerParagraphs = [];
      
      // Add profile photo if available
      if (userProfile?.avatar_url) {
        try {
          const response = await fetch(userProfile.avatar_url);
          const blob = await response.blob();
          const arrayBuffer = await blob.arrayBuffer();
          const imageSize = template === 'modern' ? 80 : template === 'classic' ? 96 : 64;
          
          // Determine image type from blob
          const imageType = blob.type.includes('png') ? 'png' : 'jpg';
          
          sections.push(
            new Paragraph({
              alignment: template === 'classic' ? AlignmentType.CENTER : AlignmentType.LEFT,
              spacing: { after: 100 },
              children: [
                new ImageRun({
                  data: new Uint8Array(arrayBuffer),
                  transformation: {
                    width: imageSize,
                    height: imageSize,
                  },
                  type: imageType,
                } as any),
              ],
            })
          );
        } catch (error) {
          console.error('Error loading avatar:', error);
        }
      }
      
      // Header with name
      const nameSize = {
        'classic': 64,
        'modern': 56,
        'minimal': 48,
        'executive': 72,
        'creative': 64,
        'technical': 56,
        'professional': 60,
        'contemporary': 56
      }[template] || 52;
      
      const nameColor = {
        'classic': '000000',
        'modern': '2563EB',
        'minimal': '000000',
        'executive': '1a1a1a',
        'creative': 'EC4899',
        'technical': '059669',
        'professional': '1e40af',
        'contemporary': '7c3aed'
      }[template] || '000000';
      
      sections.push(
        new Paragraph({
          alignment: (template === 'classic' || template === 'executive') ? AlignmentType.CENTER : AlignmentType.LEFT,
          spacing: { after: 100 },
          children: [
            new TextRun({
              text: userName,
              bold: true,
              size: nameSize,
              color: nameColor,
              font: fontFamily,
            }),
          ],
        })
      );

      // Headline
      if (userProfile?.headline) {
        sections.push(
          new Paragraph({
            alignment: template === 'classic' ? AlignmentType.CENTER : AlignmentType.LEFT,
            spacing: { after: 100 },
            children: [
              new TextRun({
                text: userProfile.headline,
                size: 28, // 14pt
                color: '4b5563',
                font: fontFamily,
              }),
            ],
          })
        );
      }

      // Contact info
      const contactParts = [];
      if (userProfile?.phone) contactParts.push(userProfile.phone);
      if (userProfile?.location) contactParts.push(userProfile.location);
      if (userProfile?.linkedin_url) contactParts.push('LinkedIn');
      
      if (contactParts.length > 0) {
        sections.push(
          new Paragraph({
            alignment: template === 'classic' ? AlignmentType.CENTER : AlignmentType.LEFT,
            spacing: { after: 360 },
            border: template === 'modern' ? {
              bottom: {
                color: '2563EB',
                space: 1,
                size: 24,
                style: BorderStyle.SINGLE,
              },
            } : undefined,
            children: [
              new TextRun({
                text: contactParts.join(' | '),
                size: 22, // 11pt
                color: '6b7280',
                font: fontFamily,
              }),
            ],
          })
        );
      }

      // Professional Summary
      const headingSize = {
        'classic': 36,
        'modern': 34,
        'minimal': 32,
        'executive': 38,
        'creative': 36,
        'technical': 34,
        'professional': 36,
        'contemporary': 34
      }[template] || 32;

      const headingColor = {
        'classic': '000000',
        'modern': '2563EB',
        'minimal': '000000',
        'executive': '1a1a1a',
        'creative': 'EC4899',
        'technical': '059669',
        'professional': '1e40af',
        'contemporary': '7c3aed'
      }[template] || '000000';

      sections.push(
        new Paragraph({
          alignment: (template === 'classic' || template === 'executive') ? AlignmentType.CENTER : AlignmentType.LEFT,
          spacing: { before: 360, after: 160 },
          children: [
            new TextRun({
              text: 'PROFESSIONAL SUMMARY',
              bold: true,
              size: headingSize,
              color: headingColor,
              font: fontFamily,
            }),
          ],
        }),
        new Paragraph({
          alignment: (template === 'classic' || template === 'executive') ? AlignmentType.CENTER : AlignmentType.LEFT,
          spacing: { after: 280 },
          children: [
            new TextRun({
              text: resumeData.summary,
              size: 24,
              font: fontFamily,
              color: '000000',
            }),
          ],
        })
      );

      // Work Experience sections
      for (const section of resumeData.sections || []) {
        sections.push(
          new Paragraph({
            alignment: (template === 'classic' || template === 'executive') ? AlignmentType.CENTER : AlignmentType.LEFT,
            spacing: { before: 360, after: 200 },
            children: [
              new TextRun({
                text: section.title.toUpperCase(),
                bold: true,
                size: headingSize,
                color: headingColor,
                font: fontFamily,
              }),
            ],
          })
        );

        for (const item of section.items || []) {
          // Job title
          sections.push(
            new Paragraph({
              spacing: { before: 200, after: 80 },
              children: [
                new TextRun({
                  text: item.title,
                  bold: true,
                  size: 26, // 13pt
                  font: fontFamily,
                  color: '000000',
                }),
              ],
            })
          );
          
          // Company name and dates
          sections.push(
            new Paragraph({
              spacing: { after: 100 },
              children: [
                new TextRun({
                  text: item.company,
                  bold: true,
                  size: 24, // 12pt
                  font: fontFamily,
                  color: '000000',
                }),
                new TextRun({
                  text: '        ',
                  size: 24,
                  font: fontFamily,
                }),
                new TextRun({
                  text: `${item.start} - ${item.end}`,
                  size: 22, // 11pt
                  color: '6b7280',
                  font: fontFamily,
                }),
              ],
            })
          );

          // Bullets
          for (const bullet of item.bullets || []) {
            sections.push(
              new Paragraph({
                text: bullet,
                bullet: template !== 'classic' ? { level: 0 } : undefined,
                spacing: { after: 100 },
                indent: { left: 360 },
                children: [
                  new TextRun({
                    text: template === 'classic' ? `• ${bullet}` : bullet,
                    size: 22, // 11pt
                    font: fontFamily,
                    color: '000000',
                  }),
                ],
              })
            );
          }
        }
      }

      // Education
      if (resumeData.education && resumeData.education.length > 0) {
        sections.push(
          new Paragraph({
            alignment: (template === 'classic' || template === 'executive') ? AlignmentType.CENTER : AlignmentType.LEFT,
            spacing: { before: 360, after: 200 },
            children: [
              new TextRun({
                text: 'EDUCATION',
                bold: true,
                size: headingSize,
                color: headingColor,
                font: fontFamily,
              }),
            ],
          })
        );

        for (const edu of resumeData.education) {
          sections.push(
            new Paragraph({
              spacing: { before: 200, after: 80 },
              children: [
                new TextRun({
                  text: edu.degree,
                  bold: true,
                  size: 26, // 13pt
                  font: fontFamily,
                  color: '000000',
                }),
              ],
            }),
            new Paragraph({
              spacing: { after: 200 },
              children: [
                new TextRun({
                  text: edu.institution,
                  bold: true,
                  size: 24, // 12pt
                  font: fontFamily,
                  color: '000000',
                }),
                new TextRun({
                  text: '        ',
                  size: 24,
                  font: fontFamily,
                }),
                new TextRun({
                  text: edu.years,
                  size: 22, // 11pt
                  color: '6b7280',
                  font: fontFamily,
                }),
              ],
            })
          );
        }
      }

      // Skills
      if (resumeData.skills.length > 0) {
        sections.push(
          new Paragraph({
            alignment: (template === 'classic' || template === 'executive') ? AlignmentType.CENTER : AlignmentType.LEFT,
            spacing: { before: 360, after: 200 },
            children: [
              new TextRun({
                text: 'SKILLS',
                bold: true,
                size: headingSize,
                color: headingColor,
                font: fontFamily,
              }),
            ],
          }),
          new Paragraph({
            alignment: (template === 'classic' || template === 'executive') ? AlignmentType.CENTER : AlignmentType.LEFT,
            spacing: { after: 200 },
            children: [
              new TextRun({
                text: resumeData.skills.join(' • '),
                size: 24,
                font: fontFamily,
                color: '000000',
              }),
            ],
          })
        );
      }

      const doc = new Document({
        sections: [{
          properties: {
            page: {
              margin: {
                top: 720,    // 0.5 inch
                bottom: 720, // 0.5 inch
                left: 1080,  // 0.75 inch
                right: 1080, // 0.75 inch
              },
            },
          },
          children: sections,
        }],
      });

      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `resume-${userName.replace(/\s+/g, '-')}.docx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to Word:', error);
      toast({
        variant: "destructive",
        title: "Export failed",
        description: "Failed to export to Word format",
      });
    }
  };

  const calculateATSScore = (data: ResumeData): number => {
    // Simple keyword-based ATS score
    const text = JSON.stringify(data).toLowerCase();
    const keywords = ['experience', 'achievement', 'managed', 'developed', 'led', 'improved', 'increased', 'reduced'];
    const matches = keywords.filter(kw => text.includes(kw)).length;
    return Math.min(100, Math.floor((matches / keywords.length) * 100) + 40);
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Resume Builder</h1>
          <p className="text-muted-foreground mt-2">Create your ATS-optimized resume</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Editor Panel */}
          <Card className="p-6">
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button 
                  onClick={generateResume} 
                  disabled={generating || credits < 1}
                  className="flex-1"
                >
                  {generating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="mr-2 h-4 w-4" />
                      Generate Draft
                    </>
                  )}
                </Button>
              </div>

              {resumeData && (
                <Tabs defaultValue="summary">
                  <TabsList className="w-full grid grid-cols-4">
                    <TabsTrigger value="summary">Summary</TabsTrigger>
                    <TabsTrigger value="experience">Experience</TabsTrigger>
                    <TabsTrigger value="education">Education</TabsTrigger>
                    <TabsTrigger value="skills">Skills</TabsTrigger>
                  </TabsList>

                  <TabsContent value="summary" className="space-y-4">
                    <div>
                      <Label>Professional Summary</Label>
                      <Textarea
                        value={resumeData.summary}
                        onChange={(e) => setResumeData({...resumeData, summary: e.target.value})}
                        rows={4}
                        className="mt-2"
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="experience" className="space-y-4">
                    {resumeData.sections.map((section, sectionIdx) => (
                      <div key={sectionIdx} className="space-y-4">
                        <div>
                          <Label>Section Title</Label>
                          <Input
                            value={section.title}
                            onChange={(e) => {
                              const newSections = [...resumeData.sections];
                              newSections[sectionIdx].title = e.target.value;
                              setResumeData({...resumeData, sections: newSections});
                            }}
                            className="mt-2"
                          />
                        </div>
                        {section.items.map((item, itemIdx) => (
                          <div key={itemIdx} className="pl-4 border-l-2 border-primary/20 space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">Job Title</Label>
                                <Input
                                  value={item.title}
                                  onChange={(e) => {
                                    const newSections = [...resumeData.sections];
                                    newSections[sectionIdx].items[itemIdx].title = e.target.value;
                                    setResumeData({...resumeData, sections: newSections});
                                  }}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Company</Label>
                                <Input
                                  value={item.company}
                                  onChange={(e) => {
                                    const newSections = [...resumeData.sections];
                                    newSections[sectionIdx].items[itemIdx].company = e.target.value;
                                    setResumeData({...resumeData, sections: newSections});
                                  }}
                                  className="mt-1"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">Start Date</Label>
                                <Input
                                  value={item.start}
                                  onChange={(e) => {
                                    const newSections = [...resumeData.sections];
                                    newSections[sectionIdx].items[itemIdx].start = e.target.value;
                                    setResumeData({...resumeData, sections: newSections});
                                  }}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">End Date</Label>
                                <Input
                                  value={item.end}
                                  onChange={(e) => {
                                    const newSections = [...resumeData.sections];
                                    newSections[sectionIdx].items[itemIdx].end = e.target.value;
                                    setResumeData({...resumeData, sections: newSections});
                                  }}
                                  className="mt-1"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Achievements</Label>
                              {item.bullets.map((bullet, bIdx) => (
                                <Textarea
                                  key={bIdx}
                                  value={bullet}
                                  onChange={(e) => {
                                    const newSections = [...resumeData.sections];
                                    newSections[sectionIdx].items[itemIdx].bullets[bIdx] = e.target.value;
                                    setResumeData({...resumeData, sections: newSections});
                                  }}
                                  rows={2}
                                  className="mt-2"
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newSections = [...resumeData.sections];
                            newSections[sectionIdx].items.push({
                              title: 'New Position',
                              company: 'Company Name',
                              start: 'Start Date',
                              end: 'End Date',
                              bullets: ['• Achievement or responsibility']
                            });
                            setResumeData({...resumeData, sections: newSections});
                          }}
                          className="w-full"
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Add Job
                        </Button>
                      </div>
                    ))}
                  </TabsContent>

                  <TabsContent value="education" className="space-y-4">
                    {resumeData.education && resumeData.education.length > 0 ? (
                      resumeData.education.map((edu, eduIdx) => (
                        <div key={eduIdx} className="pl-4 border-l-2 border-primary/20 space-y-3">
                          <div>
                            <Label className="text-xs">Degree</Label>
                            <Input
                              value={edu.degree}
                              onChange={(e) => {
                                const newEducation = [...resumeData.education];
                                newEducation[eduIdx].degree = e.target.value;
                                setResumeData({...resumeData, education: newEducation});
                              }}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Institution</Label>
                            <Input
                              value={edu.institution}
                              onChange={(e) => {
                                const newEducation = [...resumeData.education];
                                newEducation[eduIdx].institution = e.target.value;
                                setResumeData({...resumeData, education: newEducation});
                              }}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Years</Label>
                            <Input
                              value={edu.years}
                              onChange={(e) => {
                                const newEducation = [...resumeData.education];
                                newEducation[eduIdx].years = e.target.value;
                                setResumeData({...resumeData, education: newEducation});
                              }}
                              className="mt-1"
                              placeholder="e.g., 2015-2019"
                            />
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">No education data available</p>
                    )}
                  </TabsContent>

                  <TabsContent value="skills" className="space-y-4">
                    <div>
                      <Label>Skills (one per line)</Label>
                      <Textarea
                        value={resumeData.skills.join('\n')}
                        onChange={(e) => {
                          const skills = e.target.value.split('\n').filter(s => s.trim());
                          setResumeData({...resumeData, skills});
                        }}
                        rows={10}
                        className="mt-2"
                        placeholder="Enter each skill on a new line"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {resumeData.skills.map((skill, idx) => (
                        <Badge key={idx} variant="secondary">{skill}</Badge>
                      ))}
                    </div>
                  </TabsContent>
                </Tabs>
              )}
            </div>
          </Card>

          {/* Preview Panel */}
          <Card className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-start gap-4">
                <h2 className="text-xl font-semibold">Preview</h2>
                <div className="flex flex-wrap gap-2 max-w-md justify-end">
                  {['classic', 'modern', 'minimal', 'executive', 'creative', 'technical', 'professional', 'contemporary'].map((t) => (
                    <Button
                      key={t}
                      size="sm"
                      variant={template === t ? 'default' : 'outline'}
                      onClick={() => setTemplate(t as any)}
                    >
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

              {resumeData ? (
                <div 
                  className={`bg-white text-black p-8 rounded-lg min-h-[600px] shadow-sm select-none ${
                    {
                      'classic': 'font-serif',
                      'modern': 'font-sans',
                      'minimal': 'font-sans',
                      'executive': 'font-serif',
                      'creative': 'font-sans',
                      'technical': 'font-mono',
                      'professional': 'font-serif',
                      'contemporary': 'font-sans'
                    }[template]
                  }`}
                  onCopy={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <div className="space-y-4">
                    {/* Header with name and contact */}
                    <div className={`pb-4 ${
                      {
                        'classic': 'border-b-2 border-gray-800 text-center',
                        'modern': 'border-b-4 border-blue-600',
                        'minimal': 'border-b border-gray-300',
                        'executive': 'border-b-4 border-gray-900 bg-gray-50 p-6 -mx-8 mb-6',
                        'creative': 'border-l-8 border-pink-500 pl-4 bg-gradient-to-r from-pink-50 to-white -mx-8 px-12 py-4',
                        'technical': 'border-2 border-green-500 p-4 bg-green-50 -mx-8 mx-0',
                        'professional': 'border-b-2 border-blue-800 bg-blue-50 p-4 -mx-8 px-8',
                        'contemporary': 'border-b-4 border-purple-600 bg-gradient-to-b from-purple-50 to-white p-4 -mx-8 px-8'
                      }[template]
                    }`}>
                      <div className={`flex items-start gap-6 ${
                        (template === 'classic' || template === 'executive') ? 'flex-col items-center' : ''
                      }`}>
                        {userProfile?.avatar_url && (
                          <img 
                            src={userProfile.avatar_url} 
                            alt={userName}
                            className={`rounded-full object-cover ${
                              {
                                'classic': 'w-24 h-24',
                                'modern': 'w-20 h-20',
                                'minimal': 'w-16 h-16',
                                'executive': 'w-28 h-28',
                                'creative': 'w-20 h-20 ring-4 ring-pink-500',
                                'technical': 'w-20 h-20 ring-4 ring-green-500',
                                'professional': 'w-20 h-20',
                                'contemporary': 'w-20 h-20 ring-4 ring-purple-500'
                              }[template]
                            }`}
                          />
                        )}
                        <div className={`flex-1 ${(template === 'classic' || template === 'executive') ? 'text-center' : ''}`}>
                          <h2 className={`font-bold ${
                            {
                              'classic': 'text-4xl',
                              'modern': 'text-3xl text-blue-600',
                              'minimal': 'text-2xl',
                              'executive': 'text-5xl tracking-wide',
                              'creative': 'text-4xl text-pink-500 font-black',
                              'technical': 'text-3xl text-green-600 font-semibold',
                              'professional': 'text-3xl text-blue-900',
                              'contemporary': 'text-3xl text-purple-700 font-bold'
                            }[template]
                          }`}>{userName}</h2>
                          {userProfile?.headline && (
                            <p className={`text-sm text-gray-600 mt-1 ${
                              template === 'creative' ? 'italic' : ''
                            } ${template === 'executive' ? 'font-medium' : ''}`}>
                              {userProfile.headline}
                            </p>
                          )}
                          <div className={`flex gap-4 text-xs text-gray-500 mt-2 ${
                            (template === 'classic' || template === 'executive') ? 'justify-center' : ''
                          }`}>
                            {userProfile?.phone && <span>{userProfile.phone}</span>}
                            {userProfile?.location && <span>{userProfile.location}</span>}
                            {userProfile?.linkedin_url && <span>LinkedIn</span>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Professional Summary */}
                    <div>
                      <h3 className={`font-semibold mb-2 ${
                        {
                          'classic': 'text-xl text-center uppercase tracking-wide',
                          'modern': 'text-lg text-blue-600 border-l-4 border-blue-600 pl-2',
                          'minimal': 'text-base uppercase tracking-wider border-b border-gray-300 pb-1',
                          'executive': 'text-xl text-center uppercase tracking-widest border-b-2 border-gray-900 pb-2 bg-gray-50 -mx-8 px-8 py-2',
                          'creative': 'text-xl text-pink-500 border-l-4 border-pink-500 pl-3 bg-pink-50 -mx-8 px-11 py-2',
                          'technical': 'text-base text-green-600 border border-green-500 px-3 py-2 bg-green-50 font-mono uppercase tracking-wide',
                          'professional': 'text-lg text-blue-900 border-b-2 border-blue-800 pb-1 bg-blue-50 -mx-8 px-8 py-2',
                          'contemporary': 'text-lg text-purple-700 border-l-4 border-purple-600 pl-3 bg-gradient-to-r from-purple-50 to-white -mx-8 px-11 py-2'
                        }[template]
                      }`}>Professional Summary</h3>
                      <p className={`text-sm ${
                        (template === 'classic' || template === 'executive') ? 'text-center' : ''
                      } ${template === 'creative' ? 'italic pl-4 border-l-4 border-pink-100' : ''}`}>
                        {resumeData.summary}
                      </p>
                    </div>

                    {/* Work Experience */}
                    {resumeData.sections.map((section, idx) => (
                      <div key={idx}>
                        <h3 className={`font-semibold mb-3 ${
                          {
                            'classic': 'text-xl text-center uppercase tracking-wide border-b-2 border-gray-300 pb-1',
                            'modern': 'text-lg text-blue-600 border-l-4 border-blue-600 pl-2',
                            'minimal': 'text-base uppercase tracking-wider border-b border-gray-300 pb-1',
                            'executive': 'text-xl text-center uppercase tracking-widest border-b-2 border-gray-900 pb-2 bg-gray-50 -mx-8 px-8 py-2',
                            'creative': 'text-xl text-pink-500 border-l-4 border-pink-500 pl-3 bg-pink-50 -mx-8 px-11 py-2',
                            'technical': 'text-base text-green-600 border border-green-500 px-3 py-2 bg-green-50 font-mono uppercase tracking-wide',
                            'professional': 'text-lg text-blue-900 border-b-2 border-blue-800 pb-1 bg-blue-50 -mx-8 px-8 py-2',
                            'contemporary': 'text-lg text-purple-700 border-l-4 border-purple-600 pl-3 bg-gradient-to-r from-purple-50 to-white -mx-8 px-11 py-2'
                          }[template]
                        }`}>{section.title}</h3>
                        {section.items.map((item, itemIdx) => (
                          <div key={itemIdx} className={`mb-4 ${
                            (template === 'modern' || template === 'creative') ? 'pl-4' : ''
                          } ${template === 'technical' ? 'border-l-2 border-green-200 pl-4' : ''}`}>
                            <div className="flex justify-between items-start">
                              <div>
                                <p className={`font-semibold ${
                                  (template === 'modern' || template === 'executive') ? 'text-base' : 'text-sm'
                                }`}>
                                  {item.title}
                                </p>
                                <p className="text-sm text-gray-600 italic">{item.company}</p>
                              </div>
                              <p className="text-xs text-gray-500">{item.start} - {item.end}</p>
                            </div>
                            <ul className={`text-sm mt-2 space-y-1 ${
                              template === 'classic' ? 'list-none' : 'list-disc list-inside'
                            }`}>
                              {item.bullets.map((bullet, bIdx) => (
                                <li key={bIdx} className={template === 'classic' ? 'before:content-["•"] before:mr-2' : ''}>
                                  {bullet}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ))}

                    {/* Education */}
                    {resumeData.education && resumeData.education.length > 0 && (
                      <div>
                        <h3 className={`font-semibold mb-3 ${
                          {
                            'classic': 'text-xl text-center uppercase tracking-wide border-b-2 border-gray-300 pb-1',
                            'modern': 'text-lg text-blue-600 border-l-4 border-blue-600 pl-2',
                            'minimal': 'text-base uppercase tracking-wider border-b border-gray-300 pb-1',
                            'executive': 'text-xl text-center uppercase tracking-widest border-b-2 border-gray-900 pb-2 bg-gray-50 -mx-8 px-8 py-2',
                            'creative': 'text-xl text-pink-500 border-l-4 border-pink-500 pl-3 bg-pink-50 -mx-8 px-11 py-2',
                            'technical': 'text-base text-green-600 border border-green-500 px-3 py-2 bg-green-50 font-mono uppercase tracking-wide',
                            'professional': 'text-lg text-blue-900 border-b-2 border-blue-800 pb-1 bg-blue-50 -mx-8 px-8 py-2',
                            'contemporary': 'text-lg text-purple-700 border-l-4 border-purple-600 pl-3 bg-gradient-to-r from-purple-50 to-white -mx-8 px-11 py-2'
                          }[template]
                        }`}>Education</h3>
                        {resumeData.education.map((edu, idx) => (
                          <div key={idx} className={`mb-3 ${
                            (template === 'modern' || template === 'creative') ? 'pl-4' : ''
                          } ${template === 'technical' ? 'border-l-2 border-green-200 pl-4' : ''}`}>
                            <div className="flex justify-between items-start">
                              <div>
                                <p className={`font-semibold ${
                                  (template === 'modern' || template === 'executive') ? 'text-base' : 'text-sm'
                                }`}>
                                  {edu.degree}
                                </p>
                                <p className="text-sm text-gray-600 italic">{edu.institution}</p>
                              </div>
                              <p className="text-xs text-gray-500">{edu.years}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Skills */}
                    {resumeData.skills.length > 0 && (
                      <div>
                        <h3 className={`font-semibold mb-2 ${
                          {
                            'classic': 'text-xl text-center uppercase tracking-wide border-b-2 border-gray-300 pb-1',
                            'modern': 'text-lg text-blue-600 border-l-4 border-blue-600 pl-2',
                            'minimal': 'text-base uppercase tracking-wider border-b border-gray-300 pb-1',
                            'executive': 'text-xl text-center uppercase tracking-widest border-b-2 border-gray-900 pb-2 bg-gray-50 -mx-8 px-8 py-2',
                            'creative': 'text-xl text-pink-500 border-l-4 border-pink-500 pl-3 bg-pink-50 -mx-8 px-11 py-2',
                            'technical': 'text-base text-green-600 border border-green-500 px-3 py-2 bg-green-50 font-mono uppercase tracking-wide',
                            'professional': 'text-lg text-blue-900 border-b-2 border-blue-800 pb-1 bg-blue-50 -mx-8 px-8 py-2',
                            'contemporary': 'text-lg text-purple-700 border-l-4 border-purple-600 pl-3 bg-gradient-to-r from-purple-50 to-white -mx-8 px-11 py-2'
                          }[template]
                        }`}>Skills</h3>
                        <p className={`text-sm ${
                          (template === 'modern' || template === 'creative') ? 'pl-4' : ''
                        } ${(template === 'classic' || template === 'executive') ? 'text-center' : ''}
                        ${template === 'technical' ? 'font-mono' : ''}`}>
                          {resumeData.skills.join(' • ')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[600px] text-muted-foreground">
                  <p>Generate a resume to see the preview</p>
                </div>
              )}

              {resumeData && (
                <div className="flex gap-2">
                  <Button onClick={saveVersion} disabled={loading} className="flex-1">
                    <Save className="mr-2 h-4 w-4" />
                    Save Version
                  </Button>
                  <Button onClick={exportToPDF} variant="outline">
                    <FileDown className="mr-2 h-4 w-4" />
                    PDF
                  </Button>
                  <Button onClick={exportToWord} variant="outline">
                    <FileDown className="mr-2 h-4 w-4" />
                    Word
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
